'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEllipsisV,
  faCalendarRange,
  faCaretDown,
  faEraser,
} from '@fortawesome/pro-solid-svg-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  AnalyticsYearSelector,
  AnalyticsQuarterTimePicker,
  AnalyticsSelectorDropdown,
  AnalyticsFilterButton,
  AnalyticsViewButton,
  AnalyticsExportButton,
} from '../../components';
import { ClearGeographyDialog } from '../ClearGeographyDialog';
import { EncountersDrawer } from 'src/components/Encounters';
import { useAnalytics } from 'src/hooks/analytics';
import { AvailableDataRange } from 'src/utils/quarterUtils';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useAppDispatch } from 'src/redux';
import { setActiveLocation } from 'src/redux/slices/analyticsSlice';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { useLogging } from 'src/context/logging';
import { EncountersActionTypes, initialEncountersState } from 'src/context/reducers/encountersReducer';
import { PulseButton, Tooltip } from 'src/components';
import { ANALYTICS_DEFAULT_DATE_RANGE } from 'src/components/constants';
import {
  HEADER_INLINE_CLASS,
  HEADER_OVERFLOW_ITEM_CLASS,
  HEADER_OVERFLOW_TRIGGER_CLASS,
  type HeaderBreakpoint,
} from '../../constants';
import { getHeaderOverflowLayout } from '../../utils';

interface AnalyticsTableHeaderProps {
  showModeSelector?: boolean;
  showYearSelector?: boolean;
  showVolumeToggle?: boolean;
  showFilterButton?: boolean;
  showExportButton?: boolean;
  /** Adds "Configure ASP" as a section of the Options panel. */
  showAspButton?: boolean;
  showProviderSitesToggle?: boolean;
  /**
   * Adds the Payer Type / Payer Name breakdown toggle to the View panel. Only
   * the Payer Mix and Average Reimbursement reports support payer-name grouping.
   */
  showPayerGroupToggle?: boolean;
  disabledYear?: { year: string; tooltip: string };
  /**
   * Overrides the quarter picker's available data window — used by Reimbursement,
   * whose selectable quarters are restricted to a narrower range.
   */
  quarterPickerRange?: AvailableDataRange;
  /**
   * Marks `quarterPickerRange` as authoritative so the picker shows a loading
   * state (rather than the default window) while that range loads.
   */
  restrictQuarterPickerRange?: boolean;
  columns: string[];
  allowMultiple?: boolean;
  location?: string;
  noteText?: React.ReactNode;
  onExport?: (format: 'csv' | 'xlsx') => void;
}

/** A null breakpoint means the control never collapses, so it needs no variant. */
const inlineClass = (breakpoint: HeaderBreakpoint | null) =>
  breakpoint ? HEADER_INLINE_CLASS[breakpoint] : undefined;

const AnalyticsTableHeader = ({
  showModeSelector = true,
  showYearSelector = true,
  showVolumeToggle = true,
  showFilterButton = true,
  showAspButton = false,
  showExportButton = true,
  showProviderSitesToggle = false,
  showPayerGroupToggle = false,
  disabledYear,
  quarterPickerRange,
  restrictQuarterPickerRange = false,
  columns = [],
  allowMultiple = false,
  location,
  noteText,
  onExport,
}: AnalyticsTableHeaderProps) => {
  // Drawer actions
  const { closeAllButCurrent, openDrawer } = useDrawer();
  const dispatch = useAppDispatch();

  // Register this table as the active analytics location so the shared filter
  // drawer can read the in-view table's mode (each table owns its own location).
  useEffect(() => {
    if (location) {
      dispatch(setActiveLocation(location));
    }
  }, [dispatch, location]);

  const { dispatch: encountersDispatch } = useEncounters();
  const log = useLogging();
  const { quarterlyTaData } = useFlags();
  const {
    modeType = 'treating',
    volumeTypes = [],
    date_range = ANALYTICS_DEFAULT_DATE_RANGE,
    selectedGeographies = [],
    selectedCodeSet = [],
    setDateRange,
    clearSelectedGeographies,
    setSelectedCodeSet,
  } = useAnalytics(location) || {};

  // Where each collapsible control lives at the current container width. The
  // time slot is occupied either by a picker or by the disabled-year pill.
  const canExport = showExportButton && !!onExport;
  const overflowLayout = getHeaderOverflowLayout({
    hasTime: showYearSelector || !!disabledYear,
    hasExport: canExport,
    hasViewControls:
      showModeSelector ||
      showVolumeToggle ||
      showProviderSitesToggle ||
      showPayerGroupToggle ||
      showAspButton,
  });

  // Clear geography dialog state
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const hasGeographies = selectedGeographies.length > 0;

  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleDateRangeChange = useCallback(
    (gte: string, lte: string) => {
      log.event('Analytics date range changed', {
        source: 'AnalyticsTableHeader',
        location,
        gte,
        lte,
      });
      setDateRange({ gte, lte });
    },
    [setDateRange, log, location]
  );

  const handleToggleDropdown = useCallback(() => {
    log.event('Analytics header dropdown toggled', {
      source: 'AnalyticsTableHeader',
      location,
      isOpening: !isDropdownOpen,
    });
    if (!isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        right: window.innerWidth - rect.right,
      });
    }
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen, log, location]);

  // Referring volume isn't reported by site, so Sites and Referring are mutually
  // exclusive. Whichever is already selected disables the other, so the invalid
  // combination can't be reached from either direction.
  const isSitesSelected = volumeTypes.includes('sites');

  // Tables that pick a mode per code set (allowMultiple) hide the mode toggle, so
  // `modeType` is not what the user is choosing there — reading it unconditionally
  // would let a stale persisted `sending` disable Sites even with nothing selected.
  // Resolve each code set's mode the same way AnalyticsCodeSetsTable does, so a
  // legacy code set with no `mode` of its own still falls back to `modeType`.
  const hasReferringSelection = allowMultiple
    ? selectedCodeSet.some(
        (codeSet: any) => (codeSet?.mode || modeType) === 'sending'
      )
    : modeType === 'sending';

  const isModeDisabled = useCallback(
    (value: string) => value === 'sending' && isSitesSelected,
    [isSitesSelected]
  );

  const isVolumeTypeDisabled = useCallback(
    (value: string) => value === 'sites' && hasReferringSelection,
    [hasReferringSelection]
  );

  // Rendered twice — as the Options popover in the row, and flat inside the
  // "..." menu once the row runs out of space.
  const viewControls = (layout: 'auto' | 'expanded') => (
    <AnalyticsViewButton
      location={location}
      layout={layout}
      showModeSelector={showModeSelector}
      showVolumeToggle={showVolumeToggle}
      showProviderSitesToggle={showProviderSitesToggle}
      showPayerGroupToggle={showPayerGroupToggle}
      showAspButton={showAspButton}
      isModeDisabled={isModeDisabled}
      isVolumeTypeDisabled={isVolumeTypeDisabled}
    />
  );

  const handleOpenTerritoryDrawer = useCallback(() => {
    log.event('Analytics add geography button clicked', {
      source: 'AnalyticsTableHeader',
      location,
    });
    closeAllButCurrent('analytics-territory-drawer');
  }, [closeAllButCurrent, log, location]);

  const handleCreateNew = useCallback(() => {
    encountersDispatch({
      type: EncountersActionTypes.RESET_ENCOUNTERS_STATE,
      payload: initialEncountersState,
    });
    openDrawer('encountersDrawer');
  }, [encountersDispatch, openDrawer]);

  const handleCreateSuccess = useCallback((filterSet: any) => {
    const instanceId = `${filterSet.id}_${crypto.randomUUID()}`;
    setSelectedCodeSet([{ ...filterSet, instanceId, mode: 'treating' }]);
  }, [setSelectedCodeSet]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside both the button and the dropdown
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        // Don't close if clicking on a portaled dropdown (high z-index elements)
        const targetElement = event.target as HTMLElement;
        const isPortaledDropdown =
          targetElement.closest('[class*="z-[99999]"]') ||
          targetElement.closest('[class*="z-50"]');

        if (!isPortaledDropdown) {
          setIsDropdownOpen(false);
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div
      role="banner"
      className="sticky top-0 z-30 flex-shrink-0 bg-white overflow-hidden"
      data-testid="analytics-table-header"
      aria-label="Analytics table header with controls"
    >
      {/* Auto layout on purpose: the Volume cell has to stay as wide as its
          controls so the `@container/header` queries below measure the real
          available space. `table-fixed` would strip the table of its intrinsic
          width and collapse every control into the "..." menu. */}
      <table
        className="min-w-[400px] w-full border-separate border-spacing-0"
        data-testid="header-table"
        aria-label="Analytics header configuration"
      >
        <colgroup>
          {/* Must match the body tables' `272px` grid column, or the sticky
              separator drifts out of line with the header. The Geography cell's
              content fits inside 272px, so auto layout honours this width
              rather than growing past it. */}
          <col className="w-[272px] min-w-[272px]" />
          {columns.map((col) => (
            <col key={col} />
          ))}
        </colgroup>
        <tbody>
          <tr data-testid="header-title-row ">
            <td
              className="flex-shrink-0 bg-white p-4 border-r border-b border-r-neutral-200 border-b-neutral-200"
              data-testid="geography-title-cell"
            >
              <div className="flex items-center gap-2">
                <div className="justify-start text-primary text-lg font-bold font-['Inter'] leading-7">
                  Geography
                </div>
                <div className="flex items-center gap-1">
                  <PulseButton
                    variant="outline"
                    color="neutral"
                    size="xs"
                    onClick={handleOpenTerritoryDrawer}
                    data-testid="add-geography-button"
                    className="rounded-full"
                    startDecorator={faPlus}
                  >
                    Add
                  </PulseButton>
                  <Tooltip
                    content="Clear all geographies"
                    showOnCondition={hasGeographies}
                  >
                    <PulseButton
                      variant="outline"
                      color="neutral"
                      size="xs"
                      onClick={() => setIsClearDialogOpen(true)}
                      disabled={!hasGeographies}
                      data-testid="clear-geography-button"
                      aria-label="Clear all geographies"
                      className="rounded-full"
                      startDecorator={faEraser}
                    >
                      Clear
                    </PulseButton>
                  </Tooltip>
                </div>
              </div>
            </td>
            <td
              className="bg-white p-4 flex items-center border-b border-b-neutral-200"
              colSpan={columns.length}
              data-testid="volume-title-cell"
            >
              <div className="@container/header w-full flex items-center justify-between">
                <div className="justify-start text-primary text-lg font-bold font-['Inter'] leading-7">
                  Volume
                </div>

                {/* Progressive responsive controls */}
                <div
                  className="flex items-center gap-2 pl-2"
                  data-testid="header-controls"
                >
                  {/* Selector dropdown - last to collapse, first to reappear */}
                  <div
                    className={inlineClass(overflowLayout.selector)}
                    data-testid="selector-dropdown-container"
                    aria-label="Code set selector"
                  >
                    <AnalyticsSelectorDropdown
                      allowMultiple={allowMultiple}
                      location={location}
                      noteText={noteText}
                      onCreateNew={handleCreateNew}
                    />
                  </div>

                  {/* Quarter-aware picker - single combined dropdown, so it fits
                      the same slot as the legacy year selector. */}
                  {showYearSelector && !disabledYear && quarterlyTaData && (
                    <div
                      className={inlineClass(overflowLayout.time)}
                      data-testid="quarter-picker-desktop"
                      aria-label="Time period selector"
                    >
                      <AnalyticsQuarterTimePicker
                        location={location}
                        availableRange={quarterPickerRange}
                        restrictRange={restrictQuarterPickerRange}
                      />
                    </div>
                  )}

                  {/* Legacy year selector - same slot as the quarter picker */}
                  {showYearSelector && !disabledYear && !quarterlyTaData && (
                    <div
                      className={inlineClass(overflowLayout.time)}
                      data-testid="year-selector-desktop"
                      aria-label="Year range selector"
                    >
                      <AnalyticsYearSelector
                        gte={date_range.gte}
                        lte={date_range.lte}
                        onChange={handleDateRangeChange}
                      />
                    </div>
                  )}

                  {/* Disabled year pill - same slot as the quarter picker */}
                  {disabledYear && (
                    <div
                      className={inlineClass(overflowLayout.time)}
                      data-testid="disabled-year-desktop"
                      aria-label="Year range display"
                    >
                      <Tooltip content={disabledYear.tooltip}>
                        <div
                          className="px-4 bg-white rounded-full border border-neutral-200 inline-flex justify-between items-center cursor-not-allowed opacity-60 h-9"
                          data-testid="disabled-year-pill"
                          aria-disabled="true"
                        >
                          <div className="flex justify-start items-center gap-1.5 flex-1">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <Icon
                                icon={faCalendarRange}
                                className="text-secondary w-3.5 h-3"
                              />
                            </div>
                            <div className="self-stretch justify-start text-primary text-sm font-medium font-['Inter'] leading-5 line-clamp-1">
                              {disabledYear.year}
                            </div>
                          </div>
                          <Icon
                            icon={faCaretDown}
                            className="text-neutral-500 w-4 h-4 ml-2"
                          />
                        </div>
                      </Tooltip>
                    </div>
                  )}

                  {/* Options panel - holds the data-slicing controls so the row
                      stays a fixed width however many dimensions a report grows.
                      Drops into the "..." menu as soon as that menu exists. */}
                  {overflowLayout.viewControls && (
                    <div
                      className={inlineClass(overflowLayout.viewControls)}
                      data-testid="view-button-container"
                      aria-label="View controls"
                    >
                      {viewControls('auto')}
                    </div>
                  )}

                  {/* Export and Filters stay pinned as the last two controls. */}
                  {canExport && (
                    <div
                      className={inlineClass(overflowLayout.exportButton)}
                      data-testid="export-button-desktop"
                      aria-label="Export data"
                    >
                      <Tooltip
                        content="Select at least one geography to export."
                        showOnCondition={!hasGeographies}
                      >
                        <AnalyticsExportButton
                          onExport={onExport}
                          disabled={!hasGeographies}
                        />
                      </Tooltip>
                    </div>
                  )}

                  {/* Filter button - always visible */}
                  {showFilterButton && (
                    <div
                      data-testid="filter-button-container"
                      aria-label="Filter controls"
                    >
                      <AnalyticsFilterButton />
                    </div>
                  )}

                  {/* "More" dropdown - shows the inline controls once they
                      collapse. Options rides along in here, so the menu never
                      holds a lone item (see getHeaderOverflowLayout). */}
                  {overflowLayout.overflowAt && (
                    <div
                      className={
                        HEADER_OVERFLOW_TRIGGER_CLASS[overflowLayout.overflowAt]
                      }
                      data-testid="more-options-container"
                    >
                      <button
                        ref={buttonRef}
                        onClick={handleToggleDropdown}
                        className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-neutral-100 transition-colors"
                        aria-label="More options"
                        aria-expanded={isDropdownOpen}
                        aria-haspopup="true"
                        aria-controls="more-options-dropdown"
                        data-testid="more-options-button"
                      >
                        <Icon icon={faEllipsisV} className="text-primary" />
                      </button>

                      {/* Dropdown menu - portaled to body to escape header stacking context */}
                      {isDropdownOpen &&
                        dropdownPosition &&
                        ReactDOM.createPortal(
                          <div
                            ref={dropdownRef}
                            id="more-options-dropdown"
                            role="menu"
                            aria-label="Additional analytics controls"
                            className="fixed bg-white border border-neutral-200 rounded-lg shadow-lg py-2 min-w-[240px] z-50"
                            style={{
                              top: `${dropdownPosition.top + 8}px`,
                              right: `${dropdownPosition.right}px`,
                            }}
                            data-testid="more-options-dropdown"
                          >
                          {/* Options, laid out flat — nesting a dropdown inside
                              this one would just cost another click. */}
                          {overflowLayout.viewControls && (
                            <div
                              role="menuitem"
                              className={
                                HEADER_OVERFLOW_ITEM_CLASS[
                                  overflowLayout.viewControls
                                ]
                              }
                              data-testid="view-controls-mobile"
                              aria-label="View controls"
                            >
                              {viewControls('expanded')}
                            </div>
                          )}

                          {/* Quarter-aware picker */}
                          {showYearSelector &&
                            !disabledYear &&
                            quarterlyTaData &&
                            overflowLayout.time && (
                            <div
                              role="menuitem"
                              className={HEADER_OVERFLOW_ITEM_CLASS[overflowLayout.time]}
                              data-testid="quarter-picker-mobile"
                              aria-label="Time period selector"
                            >
                              <AnalyticsQuarterTimePicker
                                location={location}
                                availableRange={quarterPickerRange}
                                restrictRange={restrictQuarterPickerRange}
                              />
                            </div>
                          )}

                          {/* Legacy year selector */}
                          {showYearSelector &&
                            !disabledYear &&
                            !quarterlyTaData &&
                            overflowLayout.time && (
                            <div
                              role="menuitem"
                              className={HEADER_OVERFLOW_ITEM_CLASS[overflowLayout.time]}
                              data-testid="year-selector-mobile"
                              aria-label="Year range selector"
                            >
                              <AnalyticsYearSelector
                                gte={date_range.gte}
                                lte={date_range.lte}
                                onChange={handleDateRangeChange}
                              />
                            </div>
                          )}

                          {/* Disabled year pill */}
                          {disabledYear && overflowLayout.time && (
                            <div
                              role="menuitem"
                              className={HEADER_OVERFLOW_ITEM_CLASS[overflowLayout.time]}
                              data-testid="disabled-year-mobile"
                              aria-label="Year range display"
                            >
                              <Tooltip content={disabledYear.tooltip}>
                                <div
                                  className="px-4 bg-white rounded-full border border-neutral-200 inline-flex justify-between items-center cursor-not-allowed opacity-60 h-9"
                                  aria-disabled="true"
                                >
                                  <div className="flex justify-start items-center gap-1.5 flex-1">
                                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                      <Icon
                                        icon={faCalendarRange}
                                        className="text-secondary w-3.5 h-3"
                                      />
                                    </div>
                                    <div className="self-stretch justify-start text-primary text-sm font-medium font-['Inter'] leading-5 line-clamp-1">
                                      {disabledYear.year}
                                    </div>
                                  </div>
                                  <Icon
                                    icon={faCaretDown}
                                    className="text-neutral-500 w-4 h-4 ml-2"
                                  />
                                </div>
                              </Tooltip>
                            </div>
                          )}

                          {/* Export button */}
                          {canExport && overflowLayout.exportButton && (
                            <div
                              role="menuitem"
                              className={HEADER_OVERFLOW_ITEM_CLASS[overflowLayout.exportButton]}
                              data-testid="export-button-mobile"
                              aria-label="Export data"
                            >
                              <Tooltip
                                content="Select at least one geography to export."
                                showOnCondition={!hasGeographies}
                              >
                                <AnalyticsExportButton
                                  onExport={onExport}
                                  disabled={!hasGeographies}
                                />
                              </Tooltip>
                            </div>
                          )}

                          {/* Selector dropdown */}
                          {overflowLayout.selector && (
                            <div
                              role="menuitem"
                              className={HEADER_OVERFLOW_ITEM_CLASS[overflowLayout.selector]}
                              data-testid="selector-dropdown-mobile"
                              aria-label="Code set selector"
                            >
                              <AnalyticsSelectorDropdown
                                allowMultiple={allowMultiple}
                                location={location}
                                noteText={noteText}
                              />
                            </div>
                          )}
                          </div>,
                          document.body
                        )}
                    </div>
                  )}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <ClearGeographyDialog
        open={isClearDialogOpen}
        onClose={() => setIsClearDialogOpen(false)}
        onConfirm={clearSelectedGeographies}
      />
      <EncountersDrawer hideEncounterType onSuccess={handleCreateSuccess} />
    </div>
  );
};

export default AnalyticsTableHeader;
